/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useState, useMemo, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PropTypes from "prop-types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getBillConfigsQuery,
  getSiteIntegrationsQuery,
  getPagerDutyServices,
  getPagerDutyCanopyIntegrations,
  getSitePagerDutyServicesQuery,
  getUpkeepLocationNameQuery,
} from "../../lib/Queries";
import ManageIntegrationModal from "./ManageIntegrationModal";

const MANAGEABLE_PROVIDERS = new Set(["arcadia", "pagerduty", "upkeep"]);

const INTEGRATIONS_DATA = [
  {
    partner: "Arcadia",
    provider: "arcadia",
    dataTypes: ["Billing Periods", "Tariffs"],
  },
  {
    partner: "UpKeep",
    provider: "upkeep",
    dataTypes: ["Maintenance"],
  },
  {
    partner: "PagerDuty",
    provider: "pagerduty",
    dataTypes: ["Notifications"],
  },
];

const getArcadiaConfigSummary = (billConfigs, siteIntegrations) => {
  const preConfig = Array.isArray(billConfigs)
    ? billConfigs.find((c) => c.bill_type === "pre_microgrid" && c.active)
    : null;
  const postConfig = Array.isArray(billConfigs)
    ? billConfigs.find((c) => c.bill_type === "post_microgrid" && c.active)
    : null;

  const arcadiaIntegration = Array.isArray(siteIntegrations)
    ? siteIntegrations.find((i) => i.provider === "arcadia")
    : null;

  const calendarName = arcadiaIntegration?.config?.calendar_name || "\u2014";

  const tariffCodes = [];
  if (preConfig?.tariff_name) {
    tariffCodes.push(preConfig.tariff_name);
  } else if (preConfig?.master_tariff_id) {
    tariffCodes.push(`Tariff ${preConfig.master_tariff_id}`);
  }
  if (postConfig?.tariff_name) {
    tariffCodes.push(postConfig.tariff_name);
  } else if (postConfig?.master_tariff_id) {
    tariffCodes.push(`Tariff ${postConfig.master_tariff_id}`);
  }
  const tariffDisplay = tariffCodes.length > 0 ? tariffCodes.join(" / ") : "\u2014";

  return [calendarName, tariffDisplay];
};

const SiteIntegrations = ({ styles, theme, currentSite, organizationUuid }) => {
  const [manageProvider, setManageProvider] = useState(null);
  const queryClient = useQueryClient();

  const { data: billConfigs = [] } = useQuery({
    ...getBillConfigsQuery(currentSite?.uuid),
    enabled: !!currentSite?.uuid,
  });

  const { data: siteIntegrations = [] } = useQuery({
    ...getSiteIntegrationsQuery(currentSite?.uuid),
    enabled: !!currentSite?.uuid,
  });

  const upkeepLocationId = currentSite?.upkeep_location_id || "";

  const { data: upkeepLocationData } = useQuery({
    ...getUpkeepLocationNameQuery(upkeepLocationId),
    enabled: Boolean(upkeepLocationId),
  });

  const upkeepLocationName = upkeepLocationData?.name || "";

  const { data: pagerDutyServices = [] } = useQuery({
    queryKey: ["pd-services"],
    queryFn: getPagerDutyServices,
    enabled: !!currentSite?.uuid,
  });

  const { data: pagerDutyIntegrations = [] } = useQuery({
    queryKey: ["pd-configured"],
    queryFn: getPagerDutyCanopyIntegrations,
    enabled: !!currentSite?.uuid,
  });

  const { data: sitePdServices } = useQuery({
    ...getSitePagerDutyServicesQuery(currentSite?.uuid),
    enabled: !!currentSite?.uuid,
  });

  const handleManageClose = () => {
    if (manageProvider === "arcadia") {
      queryClient.invalidateQueries({ queryKey: ["bill-configs", currentSite?.uuid] });
      queryClient.invalidateQueries({ queryKey: ["site-integrations", currentSite?.uuid] });
    } else if (manageProvider === "pagerduty") {
      queryClient.invalidateQueries({ queryKey: ["pd-services"] });
      queryClient.invalidateQueries({ queryKey: ["pd-configured"] });
      queryClient.invalidateQueries({ queryKey: ["site-pd-services", currentSite?.uuid] });
    } else if (manageProvider === "upkeep") {
      queryClient.invalidateQueries({ queryKey: ["upkeep-location"] });
    }
    setManageProvider(null);
  };

  const pdConfiguredCount = useMemo(() => {
    return sitePdServices?.pagerduty_service_ids?.length ?? 0;
  }, [sitePdServices]);

  const getConfigDisplay = useCallback(
    (partner) => {
      if (partner === "Arcadia") {
        const parts = getArcadiaConfigSummary(billConfigs, siteIntegrations);
        return parts;
      }
      if (partner === "PagerDuty") {
        if (pdConfiguredCount === 0) return ["\u2014"];
        return [`${pdConfiguredCount} ${pdConfiguredCount === 1 ? "service" : "services"} applied`];
      }
      if (partner === "UpKeep") return [upkeepLocationName || "\u2014"];
      return ["\u2014"];
    },
    [billConfigs, siteIntegrations, upkeepLocationName, pdConfiguredCount],
  );

  const rows = useMemo(
    () =>
      INTEGRATIONS_DATA.map((row) => ({
        id: row.partner,
        partner: row.partner,
        dataTypes: row.dataTypes,
        configuration: getConfigDisplay(row.partner),
        provider: row.provider,
      })),
    [getConfigDisplay],
  );

  const renderMultiLine = (params) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {params.value.map((line, idx) => (
        <Typography key={idx} variant="body2">
          {line}
        </Typography>
      ))}
    </Box>
  );

  const columns = useMemo(
    () => [
      { field: "partner", headerName: "Partner", flex: 1, sortable: false },
      {
        field: "dataTypes",
        headerName: "Data",
        flex: 1,
        sortable: false,
        renderCell: renderMultiLine,
      },
      {
        field: "configuration",
        headerName: "Configuration",
        flex: 3,
        sortable: false,
        renderCell: renderMultiLine,
      },
      {
        field: "actions",
        headerName: "",
        width: 100,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Button
            variant="text"
            size="small"
            onClick={() => {
              if (MANAGEABLE_PROVIDERS.has(params.row.provider)) {
                setManageProvider(params.row.provider);
              }
            }}
            disabled={!MANAGEABLE_PROVIDERS.has(params.row.provider)}
            sx={{ textTransform: "none" }}
          >
            Manage
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <Box display="flex" flexDirection="column" sx={styles.settingsPanel}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: "1.5rem" }}>
        Integrations
      </Typography>

      <DataGrid
        rows={rows}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        autoHeight
        sx={{
          border: "none",
          "& .MuiDataGrid-columnSeparator": { display: "none" },
          "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
        }}
      />

      {manageProvider && (
        <ManageIntegrationModal
          provider={manageProvider}
          onClose={handleManageClose}
          currentSite={currentSite}
          billConfigs={billConfigs}
          siteIntegrations={siteIntegrations}
          pagerDutyServices={pagerDutyServices}
          pagerDutyIntegrations={pagerDutyIntegrations}
          organizationUuid={organizationUuid}
        />
      )}
    </Box>
  );
};

SiteIntegrations.propTypes = {
  styles: PropTypes.object,
  theme: PropTypes.object,
  currentSite: PropTypes.object,
  organizationUuid: PropTypes.string,
};

export default SiteIntegrations;
